import { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Section, SectionCard, SectionHeader } from '@/components/common';
import { useDelegateJobEnd } from '@/queries/useJobEnds';
import type { DelegationStatus } from '@/api/types/jobEnd.types';

interface Delegate {
  id: string;
  name: string;
  staffId: number;
}

export interface ProjectDetails {
  appraiseeName: string;
  jobEndAuthor: string;
  assignmentDate: string;
  tenureInGrade: string;
  clientName: string;
  projectName: string;
  projectCode: string;
  delegationStatus?: DelegationStatus;
  delegateName?: string;
  delegateExpiry?: string | null;
}

interface ProjectDetailsSectionProps {
  projectDetails: ProjectDetails;
  delegates?: Delegate[];
  canDelegate?: boolean;
  showDelegationStatus?: boolean;
  jobEndId?: number;
  setSnackbar?: (snackbar: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
  rowVersion?: string;
}

export function ProjectDetailsSection({
  projectDetails,
  delegates = [],
  canDelegate = false,
  showDelegationStatus = canDelegate,
  jobEndId,
  setSnackbar,
  rowVersion,
}: ProjectDetailsSectionProps) {
  const [selectedDelegate, setSelectedDelegate] = useState<Delegate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [delegateError, setDelegateError] = useState(false);
  const [refusedNotification, setRefusedNotification] = useState(false);

  const prevDelegationStatus = useRef(projectDetails.delegationStatus);

  useEffect(() => {
    if (
      prevDelegationStatus.current === 'Pending' &&
      projectDetails.delegationStatus === 'None'
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRefusedNotification(true);
      const timer = setTimeout(() => setRefusedNotification(false), 5000);
      return () => clearTimeout(timer);
    }
    prevDelegationStatus.current = projectDetails.delegationStatus;
  }, [projectDetails.delegationStatus]);

  const { mutate: delegateJobEnd, isPending } = useDelegateJobEnd();

  const handleDelegateClick = () => {
    if (!selectedDelegate) {
      setDelegateError(true);
      return;
    }
    setDelegateError(false);
    setDialogOpen(true);
  };

  const handleDelegateChange = (_: unknown, value: Delegate | null) => {
    setSelectedDelegate(value);
    if (value) {
      setDelegateError(false);
    }
  };

  const handleConfirmDelegation = () => {
    if (!selectedDelegate || !jobEndId) return;

    const delegateName = selectedDelegate.name;
    const delegateStaffId = selectedDelegate.staffId;
    setDialogOpen(false);
    setSelectedDelegate(null);

    delegateJobEnd(
      { id: jobEndId, delegateStaffId, rowVersion },
      {
        onSuccess: () => {
          setSnackbar?.({
            open: true,
            message: `Delegation request has been sent to ${delegateName} for approval.`,
            severity: 'success',
          });
        },
        onError: (error) => {
          setSnackbar?.({
            open: true,
            message: error.message || 'Failed to delegate Job End.',
            severity: 'error',
          });
        },
      }
    );
  };

  const isDelegateButtonDisabled =
    projectDetails.delegationStatus === 'Pending' ||
    projectDetails.delegationStatus === 'Approved' ||
    isPending;
  return (
    <>
      <Section>
        <SectionHeader error={delegateError}>DETAILS</SectionHeader>
        <SectionCard>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '2fr 3fr',
                md: 'repeat(3, 1fr)',
                lg: '1fr 1fr 1fr 2fr',
              },
              gap: { xs: 2, md: 3 },
            }}
          >
            <DetailItem label="Appraisee" value={projectDetails.appraiseeName} />
            <DetailItem label="Tenure in Grade" value={projectDetails.tenureInGrade} />
            <DetailItem label="Job End Author" value={projectDetails.jobEndAuthor} />
            <DetailItem label="Assignment Date" value={projectDetails.assignmentDate} />
            <DetailItem label="Client Name" value={projectDetails.clientName} />
            <DetailItem label="Project Name" value={projectDetails.projectName} />
            <DetailItem label="Project Code" value={projectDetails.projectCode} />
            {showDelegationStatus && projectDetails.delegationStatus === 'Pending' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" color="text.secondary" display="block">
                    Delegate To
                  </Typography>
                  <Typography variant="body1">
                    Pending delegation request — {projectDetails.delegateName}
                  </Typography>
                </Box>
              </Box>
            )}
            {showDelegationStatus && refusedNotification && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" color="text.secondary" display="block">
                    Delegate To
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    Delegation request was declined
                  </Typography>
                </Box>
              </Box>
            )}
            {canDelegate && projectDetails.delegationStatus !== 'Pending' && !refusedNotification && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    color={delegateError ? 'error' : 'text.secondary'}
                    display="block"
                  >
                    Delegate To
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={delegates}
                    getOptionLabel={(option) => option.name}
                    value={selectedDelegate}
                    onChange={handleDelegateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select person"
                        variant="standard"
                        error={delegateError}
                        helperText={delegateError ? 'This field is required' : ''}
                      />
                    )}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isDelegateButtonDisabled}
                  onClick={handleDelegateClick}
                  sx={{ mt: 3 }}
                >
                  Delegate
                </Button>
              </Box>
            )}
          </Box>
        </SectionCard>
      </Section>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Are you sure you want to delegate this Job End?</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedDelegate && (
              <>
                Confirming will send a request to <strong>{selectedDelegate.name}</strong>. Until they respond, the Job End will remain with you.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelegation} disabled={isPending}>
            Send request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="body1" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

export default ProjectDetailsSection;
